import { ActivatedRoute, RedirectCommand, ResolveFn, Router } from '@angular/router';
import { EnvironmentInjector, inject, Injector } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { MessageBarService, modalFn } from '@softline/ui-core';
import { TerminalInitService } from '@softapps/wws/pos-core';
import { SOFTLINE_CONFIG_WWS_CASH_REGISTER_PARAMS } from '@softapps/wws/pos-core';
import {
  TERMINAL_MODAL_INIT,
  TERMINAL_MODAL_SSE_KEY,
  TerminalModalComponent,
} from '../modules/main/modals/terminal-modal/terminal-modal.component';
import { handleRequestError } from '@softline/application';
import { map } from 'rxjs';

export const posConfigurationResolver: ResolveFn<any> = async (route) => {
  const service = inject(ApiDataAccess);
  const modal = modalFn();
  const router = inject(Router);
  const injector = inject(Injector);
  const environmentInjector = inject(EnvironmentInjector);
  const activatedRoute = inject(ActivatedRoute);
  const terminalService = inject(TerminalInitService);
  const posConfig = inject(SOFTLINE_CONFIG_WWS_CASH_REGISTER_PARAMS);
  const messageBar = inject(MessageBarService);

  let config: { terminalSseKey?: string };
  try {
    config = await service.get<{ terminalSseKey?: string }>({
      path: '/pointofsale-service/point-of-sale/configuration',
    });
  } catch (e) {
    // A rejected resolver cancels the navigation silently — the cashier would be
    // left on the launcher with no explanation. Say why and go back explicitly.
    handleRequestError(e, injector, { show: false });
    messageBar.error(
      'Die Kassa kann nicht geöffnet werden: Die Konfiguration konnte nicht geladen werden. Bitte erneut versuchen oder den Support kontaktieren.'
    );
    return new RedirectCommand(router.parseUrl('/'));
  }

  if (posConfig?.hasTerminal !== undefined && posConfig?.hasTerminal === false) {
    return config;
  } else if (route.queryParamMap.has('terminalInit')) {
    await modal({
      title: 'Bankomat initialisieren',
      subtitle: 'Verbindung zum Bankomat-Terminal wird hergestellt.',
      icon: 'fa-regular fa-credit-card',
      dismiss: false,
      component: TerminalModalComponent,
      injector: Injector.create({
        parent: environmentInjector,
        providers: [
          {
            provide: TERMINAL_MODAL_INIT,
            useValue: async (resolveFn) => {
              try {
                await terminalService.init();
                setTimeout(() => {
                  resolveFn(true);
                }, 900);
              } catch (e) {
                handleRequestError(e, injector, { show: true });
                terminalService.state.set('ERROR');
                console.error(e);
                resolveFn(false);
              }
            },
          },
          {
            provide: ActivatedRoute,
            useValue: {
              ...activatedRoute,
              data: activatedRoute.data.pipe(
                map((data) => ({
                  ...data,
                  configuration: config,
                }))
              ),
            },
          },
          {
            provide: TERMINAL_MODAL_SSE_KEY,
            useValue: config?.terminalSseKey,
          },
        ],
      }),
      data: {
        finalizeRequired: false,
      },
    });

    return config;
  } else if (!terminalService.initialized()) {
    const urlTree = router.createUrlTree(['/pos'], {
      queryParams: {
        terminalInit: true,
      },
    });

    return new RedirectCommand(urlTree);
  }

  return config;
};
